import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Minus, Plus, Ruler } from "lucide-react";
import { toast } from "sonner";
import { api, img, money } from "@/lib/api";
import { useStore } from "@/store/StoreContext";
import ProductCard from "@/components/ProductCard";
import DropCountdown from "@/components/DropCountdown";
import NotifyForm from "@/components/NotifyForm";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const STATUS_STYLE = {
  limited: "bg-gold text-noir-bg",
  sold_out: "bg-cream/10 text-cream/60 border border-cream/20",
  coming_soon: "bg-transparent text-gold border border-gold/50",
  restock: "bg-cream text-noir-bg",
};

const SIZE_GUIDE = [
  ["Size", "Chest (in)", "Length (in)", "Shoulder (in)"],
  ["S", "42", "27", "20"],
  ["M", "44", "28", "21"],
  ["L", "46", "29", "22"],
  ["XL", "48", "30", "23"],
  ["XXL", "50", "31", "24"],
];

export default function ProductPage() {
  const { slug } = useParams();
  const { addToCart, setCartOpen, wishlist, toggleWishlist } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState(null);
  const [pieceSizes, setPieceSizes] = useState({});
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const imgRef = useRef(null);

  useEffect(() => {
    setProduct(null);
    setSize(null);
    setQty(1);
    setActiveImg(0);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      api.get("/products", { params: { category: r.data.category } }).then((res) => {
        setRelated(res.data.filter((p) => p.slug !== slug).slice(0, 4));
      });
    }).catch(() => setProduct(false));
  }, [slug]);

  if (product === null) return <div className="pt-16 min-h-screen" data-testid="product-loading" />;
  if (product === false) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center" data-testid="product-not-found">
        <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-cream/40">Piece not found.</p>
      </div>
    );
  }

  const buyable = product.status === "available" || product.status === "limited" || product.status === "restock";
  const wished = wishlist.includes(product.slug);

  const onZoomMove = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin(`${x}% ${y}%`);
  };

  const validateSelection = () => {
    if (product.pieces) {
      const missing = product.pieces.filter((p) => !pieceSizes[p.slug]);
      if (missing.length) {
        toast.error(`Select a size for the ${missing[0].label}.`);
        return null;
      }
      return pieceSizes;
    }
    if (!size) {
      toast.error("Select a size first.");
      return null;
    }
    return size;
  };

  const handleAdd = () => {
    const selection = validateSelection();
    if (selection === null) return;
    if (product.pieces) addToCart(product, "Set", qty, selection);
    else addToCart(product, selection, qty);
  };

  return (
    <div className="pt-16" data-testid="product-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <nav className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-cream/40 mb-10" data-testid="product-breadcrumb">
          <Link to="/shop" className="hover:text-gold transition-colors">Shop</Link>
          <span className="mx-2">/</span>
          <Link to={`/shop?category=${product.category}`} className="hover:text-gold transition-colors">{product.category}</Link>
          <span className="mx-2">/</span>
          <span className="text-gold">{product.serial}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <motion.div
              key={activeImg}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              ref={imgRef}
              className="relative aspect-[3/4] overflow-hidden bg-noir-card cursor-crosshair"
              onMouseEnter={() => setZoom(true)}
              onMouseLeave={() => setZoom(false)}
              onMouseMove={onZoomMove}
              data-testid="product-main-image"
            >
              <img
                src={img(product.images[activeImg])}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-300 ease-out"
                style={{ transform: zoom ? "scale(1.7)" : "scale(1)", transformOrigin: origin }}
              />
              {product.badge && (
                <span className={`absolute top-4 left-4 font-mono2 text-[9px] tracking-[0.25em] uppercase px-3 py-2 ${STATUS_STYLE[product.status] || ""}`} data-testid="product-status-badge">
                  {product.badge}
                </span>
              )}
            </motion.div>
            <div className="flex gap-3 mt-3">
              {product.images.map((im, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 aspect-[3/4] overflow-hidden bg-noir-card border transition-colors duration-300 ${activeImg === i ? "border-gold" : "border-white/10 hover:border-white/30"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={img(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="lg:pt-4">
            <Reveal><Eyebrow>{product.serial} — {product.category}</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl mt-4 leading-[0.95]" data-testid="product-name">{product.name}</h1>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="font-mono2 text-xl text-gold mt-5" data-testid="product-price">{money(product.price)}</p>
            </Reveal>
            {["limited", "restock"].includes(product.status) && product.units > 0 && (
              <Reveal delay={0.17}>
                <p className="mt-3 font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold" data-testid="stock-countdown">
                  Only {product.units} left — numbered run
                </p>
              </Reveal>
            )}
            <Reveal delay={0.2}>
              <p className="mt-6 text-cream/60 text-sm leading-relaxed" data-testid="product-story">{product.story}</p>
            </Reveal>

            <Reveal delay={0.26}>
              <div className="mt-10">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/50">
                    {product.pieces ? "Size Each Piece" : "Select Size"}
                  </span>
                  <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-cream/40 flex items-center gap-2"><Ruler size={12} /> {product.gsm}</span>
                </div>
                {product.pieces ? (
                  <div className="space-y-5" data-testid="piece-size-selectors">
                    {product.pieces.map((piece) => (
                      <div key={piece.slug}>
                        <p className="font-mono2 text-[9px] tracking-[0.25em] uppercase text-gold/80 mb-2.5">{piece.label}</p>
                        <div className="flex flex-wrap gap-2" data-testid={`piece-sizes-${piece.slug}`}>
                          {piece.sizes.map((s) => (
                            <button
                              key={s}
                              onClick={() => setPieceSizes((prev) => ({ ...prev, [piece.slug]: s }))}
                              disabled={!buyable}
                              className={`min-w-[3.25rem] px-3 py-3 font-mono2 text-xs border transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-30 ${pieceSizes[piece.slug] === s ? "bg-gold text-noir-bg border-gold" : "border-white/15 text-cream/70 hover:border-gold/60 hover:text-gold"}`}
                              data-testid={`piece-size-${piece.slug}-${s.toLowerCase()}`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                <div className="flex flex-wrap gap-2" data-testid="size-selector">
                  {product.sizes.map((s) => {
                    const stock = product.size_stock ? product.size_stock[s] : undefined;
                    const soldOutSize = stock !== undefined && stock <= 0;
                    return (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        disabled={!buyable || soldOutSize}
                        title={soldOutSize ? `Sold out in ${s}` : stock !== undefined && stock <= 5 ? `Only ${stock} left in ${s}` : undefined}
                        className={`min-w-[3.25rem] px-3 py-3 font-mono2 text-xs border transition-colors duration-300 disabled:cursor-not-allowed ${
                          soldOutSize
                            ? "border-white/10 text-cream/25 line-through"
                            : size === s
                              ? "bg-gold text-noir-bg border-gold"
                              : "border-white/15 text-cream/70 hover:border-gold/60 hover:text-gold"
                        }`}
                        data-testid={`size-option-${s.toLowerCase()}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
                {product.size_stock && buyable && product.sizes.some((s) => (product.size_stock[s] ?? 1) > 0 && (product.size_stock[s] ?? 1) <= 5) && (
                  <p className="mt-3 font-mono2 text-[9px] tracking-[0.2em] uppercase text-gold/70" data-testid="low-stock-hint">
                    Low stock: {product.sizes.filter((s) => (product.size_stock[s] ?? 0) > 0 && (product.size_stock[s] ?? 0) <= 5).map((s) => `${s} (${product.size_stock[s]} left)`).join(" · ")}
                  </p>
                )}
                  </>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.32}>
              <div className="mt-8 flex gap-3">
                <div className="flex items-center border border-white/15">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-4 text-cream/60 hover:text-gold" data-testid="qty-minus"><Minus size={14} /></button>
                  <span className="px-3 font-mono2 text-sm" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="px-4 py-4 text-cream/60 hover:text-gold" data-testid="qty-plus"><Plus size={14} /></button>
                </div>
                {buyable ? (
                  <button onClick={handleAdd} className="flex-1 bg-gold text-noir-bg font-mono2 text-[11px] tracking-[0.25em] uppercase hover:bg-cream transition-colors duration-300" data-testid="add-to-cart-button">
                    Add to Cart
                  </button>
                ) : (
                  <button disabled className="flex-1 border border-white/15 text-cream/40 font-mono2 text-[11px] tracking-[0.25em] uppercase cursor-not-allowed" data-testid="unavailable-button">
                    {product.status === "sold_out" ? "Sold Out" : "Coming Soon"}
                  </button>
                )}
                <button
                  onClick={() => toggleWishlist(product)}
                  className={`px-4 border transition-colors duration-300 ${wished ? "border-gold text-gold" : "border-white/15 text-cream/60 hover:text-gold hover:border-gold/60"}`}
                  data-testid="product-wishlist-toggle"
                  aria-label="Toggle wishlist"
                >
                  <Heart size={18} strokeWidth={1.5} fill={wished ? "currentColor" : "none"} />
                </button>
              </div>
              {buyable && (
                <button
                  onClick={() => {
                    const selection = validateSelection();
                    if (selection === null) return;
                    if (product.pieces) addToCart(product, "Set", qty, selection);
                    else addToCart(product, selection, qty);
                    setCartOpen(true);
                  }}
                  className="mt-3 w-full border border-cream/30 text-cream font-mono2 text-[11px] tracking-[0.25em] uppercase py-4 hover:border-gold hover:text-gold transition-colors duration-300"
                  data-testid="buy-now-button"
                >
                  Buy Now
                </button>
              )}
              {!buyable && product.status === "coming_soon" && (
                <div className="mt-6 border border-gold/30 p-5" data-testid="drop-countdown-block">
                  <p className="font-mono2 text-[9px] tracking-[0.3em] uppercase text-gold mb-3">Drop 002 goes live in</p>
                  <DropCountdown variant="large" />
                </div>
              )}
              {!buyable && (
                <div className="mt-6">
                  <NotifyForm product={product} />
                  <p className="mt-4 text-xs text-cream/40" data-testid="drop-notify-note">
                    {product.status === "coming_soon" ? "Arrives with Drop 002." : "Gone — for now. Restocks are announced by email first."}
                  </p>
                </div>
              )}
            </Reveal>

            <Reveal delay={0.38}>
              <Accordion type="single" collapsible className="mt-10 border-t border-white/10" data-testid="product-details-accordion">
                <AccordionItem value="details" className="border-white/10">
                  <AccordionTrigger className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/70 hover:text-gold hover:no-underline" data-testid="accordion-garment-details">Garment Details</AccordionTrigger>
                  <AccordionContent className="text-sm text-cream/50 leading-relaxed">
                    <p>Fabric weight: {product.gsm}</p>
                    <p className="mt-2">{product.material}</p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="fit" className="border-white/10">
                  <AccordionTrigger className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/70 hover:text-gold hover:no-underline" data-testid="accordion-fit">Fit Information</AccordionTrigger>
                  <AccordionContent className="text-sm text-cream/50 leading-relaxed">{product.fit}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="size-guide" className="border-white/10">
                  <AccordionTrigger className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/70 hover:text-gold hover:no-underline" data-testid="accordion-size-guide">Size Guide</AccordionTrigger>
                  <AccordionContent>
                    <table className="w-full text-xs font-mono2 text-cream/60" data-testid="size-guide-table">
                      <tbody>
                        {SIZE_GUIDE.map((row, i) => (
                          <tr key={i} className={i === 0 ? "text-gold" : "border-t border-white/10"}>
                            {row.map((cell, j) => <td key={j} className="py-2 pr-4">{cell}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="care" className="border-white/10">
                  <AccordionTrigger className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/70 hover:text-gold hover:no-underline" data-testid="accordion-care">Care Instructions</AccordionTrigger>
                  <AccordionContent className="text-sm text-cream/50 leading-relaxed">{product.care}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="shipping" className="border-white/10">
                  <AccordionTrigger className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/70 hover:text-gold hover:no-underline" data-testid="accordion-shipping">Shipping</AccordionTrigger>
                  <AccordionContent className="text-sm text-cream/50 leading-relaxed">
                    Ships within 3–5 business days of the drop closing. Free US shipping over $150. Limited pieces are final sale.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Reveal>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24 sm:mt-32" data-testid="related-products">
            <Reveal><Eyebrow>Complete the Fit</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-4xl mt-4 mb-12">Related Pieces</h2>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>

      {buyable && (
        <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-noir-panel/95 backdrop-blur-md border-t border-white/10 px-4 py-3 flex items-center gap-3" data-testid="sticky-mobile-add-to-cart">
          <span className="font-mono2 text-gold text-sm">{money(product.price)}</span>
          <button onClick={handleAdd} className="flex-1 bg-gold text-noir-bg font-mono2 text-[10px] tracking-[0.25em] uppercase py-3.5 hover:bg-cream transition-colors" data-testid="sticky-add-to-cart-button">
            {product.pieces
              ? (product.pieces.every((p) => pieceSizes[p.slug]) ? "Add Full Set" : "Size Each Piece")
              : size ? `Add — ${size}` : "Select a Size"}
          </button>
        </div>
      )}
    </div>
  );
}
